/**
 * Crypto key service for auditor SECP256k1 key management
 *
 * Handles:
 * - SECP256k1 keypair generation
 * - Password-based key derivation using Argon2id (WASM) with PBKDF2 fallback
 * - Private key encryption/decryption with AES-256-GCM (AEAD)
 * - Session-based private key storage (sessionStorage, cleared on tab close)
 *
 * Firestore schema for encrypted key data:
 *   publicKey    – SECP256k1 public key (base64, 65 bytes uncompressed)
 *   encPrivKey   – AES-GCM ciphertext of the private key (base64)
 *   salt         – Random salt for KDF (base64, 32 bytes)
 *   kdfParams    – { algorithm, memoryCost, timeCost, parallelism, hashLength } or { algorithm, iterations, hash, hashLength }
 *   iv           – AES-GCM nonce (base64, 12 bytes)
 *   keyVersion   – Integer version for future key rotation
 */

import { ethers } from 'ethers';
import { Buffer } from 'buffer';

// ─── Constants ─────────────────────────────────────────────────
const SESSION_KEY = 'auditor_secp_private_key';

/** Default Argon2id parameters (64 MiB memory, 3 iterations) */
const DEFAULT_ARGON2_PARAMS = {
  algorithm: 'argon2id',
  memoryCost: 65536,    // KiB → 64 MiB
  timeCost: 3,
  parallelism: 1,
  hashLength: 32,       // 256-bit KEK
};

/** Default PBKDF2 parameters (OWASP 2024 recommendation for SHA-256) */
const DEFAULT_PBKDF2_PARAMS = {
  algorithm: 'pbkdf2',
  iterations: 600000,
  hash: 'SHA-256',
  hashLength: 32,
};

// ─── Key Generation ────────────────────────────────────────────
// Must match the old setup-auditors.js / update-auditor-public-key.js logic exactly
// so that backend btcec.Encrypt (using this public key) is decryptable with this private key.

/**
 * Generate a SECP256k1 keypair (same as old register scripts).
 * Public key: 65 bytes (0x04 || X || Y) in base64 — compatible with Go btcec.ParsePubKey.
 * Private key: ethers wallet.privateKey (0x + 64 hex chars).
 * @returns {{ publicKey: string, privateKey: string }}
 */
export function generateSecpKeypair() {
  const wallet = ethers.Wallet.createRandom();

  // In ethers v5, wallet.publicKey is 130 hex chars = 65 bytes including 0x04 prefix
  const publicKeyHex = wallet.publicKey;
  const cleanHex = publicKeyHex.startsWith('0x') ? publicKeyHex.slice(2) : publicKeyHex;
  const publicKeyBytes = Buffer.from(cleanHex, 'hex');

  // btcec.ParsePubKey expects 65 bytes (0x04 + 32 X + 32 Y) or 33 bytes compressed
  // Do NOT add another 0x04 if already 65 bytes (would make 66 = invalid)
  let bytesForBtcec = publicKeyBytes;
  if (publicKeyBytes.length === 64) {
    bytesForBtcec = Buffer.concat([Buffer.from([0x04]), publicKeyBytes]);
  } else if (publicKeyBytes.length !== 65) {
    throw new Error(`Unexpected public key length from ethers: ${publicKeyBytes.length} bytes (expected 64 or 65)`);
  }

  const publicKeyBase64 = bytesForBtcec.toString('base64');

  // Return private key in same format as old script: wallet.privateKey (0x + 64 hex)
  return {
    publicKey: publicKeyBase64,
    privateKey: wallet.privateKey,
  };
}

/**
 * Derive the public key (base64, 65-byte uncompressed) from a private key hex string.
 * This lets us verify the round-trip: privateKey → publicKey === storedPublicKey.
 * @param {string} privateKeyHex  64 hex chars (no 0x prefix)
 * @returns {string} base64-encoded 65-byte uncompressed public key
 */
export function derivePublicKeyFromPrivate(privateKeyHex) {
  const wallet = new ethers.Wallet('0x' + privateKeyHex);
  const pubHex = wallet.publicKey.startsWith('0x') ? wallet.publicKey.slice(2) : wallet.publicKey;
  const pubBytes = Buffer.from(pubHex, 'hex');

  let bytes65 = pubBytes;
  if (pubBytes.length === 64) {
    bytes65 = Buffer.concat([Buffer.from([0x04]), pubBytes]);
  }
  return bytes65.toString('base64');
}

/**
 * Verify that a private key corresponds to the given public key.
 * @param {string} privateKeyHex   64 hex chars
 * @param {string} publicKeyBase64 stored public key (base64, 65 bytes)
 * @returns {boolean}
 */
export function verifyKeyPair(privateKeyHex, publicKeyBase64) {
  try {
    const derived = derivePublicKeyFromPrivate(privateKeyHex);
    const match = derived === publicKeyBase64;
    if (!match) {
      console.error('[cryptoKeyService] KEY MISMATCH!', {
        derivedPublicKey: derived,
        storedPublicKey: publicKeyBase64,
        derivedLen: derived.length,
        storedLen: publicKeyBase64.length,
      });
    }
    return match;
  } catch (e) {
    console.error('[cryptoKeyService] verifyKeyPair error:', e);
    return false;
  }
}

// ─── Key Derivation ────────────────────────────────────────────

/**
 * Generate a cryptographically random salt.
 * @param {number} [length=32] Salt length in bytes
 * @returns {Uint8Array}
 */
function generateSalt(length = 32) {
  return crypto.getRandomValues(new Uint8Array(length));
}

/**
 * Derive a 256-bit key encryption key (KEK) from password + salt.
 * Tries Argon2id first; falls back to PBKDF2 if WASM is unavailable.
 *
 * @param {string} password          User's login password
 * @param {Uint8Array} salt          Random salt
 * @param {Object|null} [params]     Stored kdfParams (pass null to use defaults / auto-select)
 * @returns {Promise<{ key: Uint8Array, kdfParams: Object }>}
 */
export async function deriveKeyFromPassword(password, salt, params = null) {
  // If stored params specify an algorithm, honour it
  if (params && params.algorithm === 'pbkdf2') {
    return deriveKeyPbkdf2(password, salt, params);
  }

  // Try Argon2id first
  if (!params || params.algorithm === 'argon2id') {
    try {
      return await deriveKeyArgon2id(password, salt, params);
    } catch (err) {
      console.warn('[cryptoKeyService] Argon2id unavailable, falling back to PBKDF2:', err.message);
      // If the stored params *require* argon2id we cannot silently downgrade
      if (params && params.algorithm === 'argon2id') {
        throw new Error(`Argon2id required by stored key params but failed: ${err.message}`);
      }
    }
  }

  // PBKDF2 fallback
  return deriveKeyPbkdf2(password, salt, params);
}

/**
 * Derive KEK with Argon2id via argon2-browser WASM.
 */
async function deriveKeyArgon2id(password, salt, params = null) {
  const p = { ...DEFAULT_ARGON2_PARAMS, ...params };

  // Dynamic import so the main bundle doesn't break if WASM isn't available
  let argon2;
  try {
    const mod = await import('argon2-browser');
    argon2 = mod.default || mod;
  } catch (e) {
    throw new Error('argon2-browser module not available: ' + e.message);
  }

  const result = await argon2.hash({
    pass: new TextEncoder().encode(password),
    salt: salt,
    time: p.timeCost,
    mem: p.memoryCost,
    hashLen: p.hashLength,
    parallelism: p.parallelism,
    type: argon2.ArgonType.Argon2id,
  });

  return {
    key: new Uint8Array(result.hash),
    kdfParams: {
      algorithm: 'argon2id',
      memoryCost: p.memoryCost,
      timeCost: p.timeCost,
      parallelism: p.parallelism,
      hashLength: p.hashLength,
    },
  };
}

/**
 * Derive KEK with PBKDF2 via Web Crypto API.
 */
async function deriveKeyPbkdf2(password, salt, params = null) {
  const p = { ...DEFAULT_PBKDF2_PARAMS, ...params };

  const keyMaterial = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveBits'],
  );

  const derivedBits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt,
      iterations: p.iterations,
      hash: p.hash,
    },
    keyMaterial,
    p.hashLength * 8,
  );

  return {
    key: new Uint8Array(derivedBits),
    kdfParams: {
      algorithm: 'pbkdf2',
      iterations: p.iterations,
      hash: p.hash,
      hashLength: p.hashLength,
    },
  };
}

// ─── AES-256-GCM ──────────────────────────────────────────────

/**
 * Encrypt plaintext with AES-256-GCM.
 * @param {string} plaintext  Text to encrypt (private key hex)
 * @param {Uint8Array} kek    32-byte key encryption key
 * @returns {Promise<{ ciphertext: string, iv: string }>}  Both base64-encoded
 */
async function encryptAesGcm(plaintext, kek) {
  const iv = crypto.getRandomValues(new Uint8Array(12)); // 96-bit nonce

  const aesKey = await crypto.subtle.importKey(
    'raw', kek, { name: 'AES-GCM' }, false, ['encrypt'],
  );

  const ct = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    new TextEncoder().encode(plaintext),
  );

  return {
    ciphertext: Buffer.from(new Uint8Array(ct)).toString('base64'),
    iv: Buffer.from(iv).toString('base64'),
  };
}

/**
 * Decrypt AES-256-GCM ciphertext.
 * @param {string} ciphertextB64  Base64-encoded ciphertext (includes GCM auth tag)
 * @param {string} ivB64          Base64-encoded IV
 * @param {Uint8Array} kek        32-byte key encryption key
 * @returns {Promise<string>}     Plaintext string
 */
async function decryptAesGcm(ciphertextB64, ivB64, kek) {
  const ct = new Uint8Array(Buffer.from(ciphertextB64, 'base64'));
  const iv = new Uint8Array(Buffer.from(ivB64, 'base64'));

  const aesKey = await crypto.subtle.importKey(
    'raw', kek, { name: 'AES-GCM' }, false, ['decrypt'],
  );

  const pt = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv },
    aesKey,
    ct,
  );

  return new TextDecoder().decode(pt);
}

// ─── High-level API ────────────────────────────────────────────

/**
 * Generate a fresh SECP256k1 keypair, encrypt the private key with the
 * user's password, and return everything needed to store in Firestore +
 * the cleartext private key for session storage.
 *
 * @param {string} password  User's login password
 * @returns {Promise<{
 *   publicKey: string,
 *   encPrivKey: string,
 *   salt: string,
 *   kdfParams: Object,
 *   iv: string,
 *   keyVersion: number,
 *   privateKeyHex: string
 * }>}
 */
export async function generateAndEncryptKeypair(password) {
  const { publicKey, privateKey } = generateSecpKeypair();

  // Normalize to 64 hex chars (strip 0x) for storage and session — same bytes as old script
  const privateKeyHex = privateKey.startsWith('0x') ? privateKey.slice(2) : privateKey;
  if (!/^[0-9a-fA-F]{64}$/.test(privateKeyHex)) {
    throw new Error('Generated private key has wrong length or format');
  }

  // Sanity-check: derived public key must match what we store (same as old script semantics)
  if (!verifyKeyPair(privateKeyHex, publicKey)) {
    throw new Error('Generated keypair failed self-check — public key does not match private key');
  }
  console.log('[cryptoKeyService] Generated keypair verified OK');

  const salt = generateSalt(32);
  const { key: kek, kdfParams } = await deriveKeyFromPassword(password, salt);
  const { ciphertext, iv } = await encryptAesGcm(privateKeyHex, kek);

  // Round-trip check: decrypt and verify
  const roundTrip = await decryptAesGcm(ciphertext, iv, kek);
  if (roundTrip !== privateKeyHex) {
    throw new Error('AES-GCM round-trip failed — encrypted/decrypted key mismatch');
  }
  console.log('[cryptoKeyService] AES-GCM round-trip verified OK');

  return {
    publicKey,
    encPrivKey: ciphertext,
    salt: Buffer.from(salt).toString('base64'),
    kdfParams,
    iv,
    keyVersion: 1,
    privateKeyHex, // 64 hex for session storage — never persisted to DB
  };
}

/**
 * Decrypt an existing encrypted private key using the user's password
 * and the stored key material.
 *
 * @param {string} password
 * @param {{ encPrivKey: string, salt: string, kdfParams: Object, iv: string }} data
 * @returns {Promise<string>}  Private key hex (64 chars)
 */
export async function decryptPrivateKey(password, data) {
  const { encPrivKey, salt, kdfParams, iv } = data;
  if (!encPrivKey || !salt || !iv) {
    throw new Error('Incomplete encrypted key data (missing encPrivKey, salt, or iv)');
  }

  const saltBytes = new Uint8Array(Buffer.from(salt, 'base64'));
  const { key: kek } = await deriveKeyFromPassword(password, saltBytes, kdfParams);
  const privateKeyHex = await decryptAesGcm(encPrivKey, iv, kek);

  // Sanity check: must be exactly 64 hex chars
  if (!/^[0-9a-fA-F]{64}$/.test(privateKeyHex)) {
    throw new Error('Decryption produced invalid private key — wrong password?');
  }

  return privateKeyHex;
}

/**
 * Re-wrap an existing private key under a new password (new salt + IV).
 * Does not generate a new keypair — public key identity is preserved.
 *
 * @param {string} privateKeyHex  64 hex chars (no 0x)
 * @param {string} password       New password
 * @returns {Promise<{ encPrivKey: string, salt: string, kdfParams: Object, iv: string }>}
 */
export async function wrapPrivateKeyWithPassword(privateKeyHex, password) {
  const normalized = normalizePrivateKeyHex(privateKeyHex);
  if (!normalized) {
    throw new Error('Invalid private key format for re-wrap');
  }
  if (!password || typeof password !== 'string') {
    throw new Error('Password is required to wrap private key');
  }

  const salt = generateSalt(32);
  const { key: kek, kdfParams } = await deriveKeyFromPassword(password, salt);
  const { ciphertext, iv } = await encryptAesGcm(normalized, kek);

  const roundTrip = await decryptAesGcm(ciphertext, iv, kek);
  if (roundTrip !== normalized) {
    throw new Error('AES-GCM round-trip failed during key re-wrap');
  }

  return {
    encPrivKey: ciphertext,
    salt: Buffer.from(salt).toString('base64'),
    kdfParams,
    iv,
  };
}

// ─── Session Storage ───────────────────────────────────────────

/** In-memory fallback when sessionStorage is unavailable (e.g. Safari private mode) */
let _inMemoryKey = null;

/**
 * Normalize private key to exactly 64 hex chars (32 bytes). SECP256k1 private keys
 * are 32 bytes; a leading "04" (uncompressed public key prefix) would make ECDH/MAC fail.
 * @param {string} raw - hex with optional 0x or erroneous leading 04
 * @returns {string|null} 64 hex chars or null
 */
function normalizePrivateKeyHex(raw) {
  if (!raw || typeof raw !== 'string') return null;
  let hex = raw.replace(/^0x/i, '').trim();
  // Strip erroneous leading "04" (uncompressed public key prefix) if key is 66 chars
  if (hex.length === 66 && hex.toLowerCase().startsWith('04')) {
    hex = hex.slice(2);
  }
  if (hex.length !== 64 || !/^[0-9a-fA-F]+$/.test(hex)) return null;
  return hex.toLowerCase();
}

/**
 * Store the decrypted private key for this browser session (always 64 hex).
 * Uses sessionStorage (cleared when the tab is closed).
 * @param {string} privateKeyHex - 64 hex chars, or 0x-prefixed, or 66 chars with erroneous leading 04
 */
export function storePrivateKeyInSession(privateKeyHex) {
  const normalized = normalizePrivateKeyHex(privateKeyHex);
  if (!normalized) {
    console.warn('[cryptoKeyService] storePrivateKeyInSession: invalid key length/format, not storing');
    return;
  }
  try {
    sessionStorage.setItem(SESSION_KEY, normalized);
  } catch {
    _inMemoryKey = normalized;
  }
}

/**
 * Retrieve the private key from session storage (always normalized to 64 hex).
 * @returns {string|null}
 */
export function getPrivateKeyFromSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY) || _inMemoryKey;
    return normalizePrivateKeyHex(raw);
  } catch {
    return normalizePrivateKeyHex(_inMemoryKey);
  }
}

/**
 * Clear the private key from session storage and in-memory fallback.
 */
export function clearPrivateKeyFromSession() {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
  _inMemoryKey = null;
}

/**
 * Check whether a private key is available in the current session.
 * @returns {boolean}
 */
export function hasPrivateKeyInSession() {
  return !!getPrivateKeyFromSession();
}
